import { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import useProjectStore from "../../store/projectStore";
import { bladesApi, geometryApi } from "../../api/blades";
import useSaveShortcut from "../../hooks/useSaveShortcut";
import useBladeReadonly from "../../hooks/useBladeReadonly";

import { useChartColors } from "../../chartTheme";

function NumInput({ label, value, onChange, disabled }) {
  return (
    <label className="flex items-center gap-1 text-sm text-gray-500">
      {label}
      <input
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="ml-1 w-24 border border-gray-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-bh-500 disabled:bg-gray-50 disabled:text-gray-400"
      />
    </label>
  );
}

export default function CutoffPage() {
  const { LINE_COLORS } = useChartColors();
  const { activeDesign, setActiveDesign } = useProjectStore();
  const { isReadOnly } = useBladeReadonly();
  const [active, setActive] = useState(false);
  const [r1, setR1] = useState("");
  const [z1, setZ1] = useState("");
  const [r2, setR2] = useState("");
  const [z2, setZ2] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [viewData, setViewData] = useState(null);

  useEffect(() => {
    if (!activeDesign) return;
    setActive(activeDesign.cutoff_active ?? false);
    setR1(activeDesign.cutoff_r?.[0] ?? "");
    setZ1(activeDesign.cutoff_z?.[0] ?? "");
    setR2(activeDesign.cutoff_r?.[1] ?? "");
    setZ2(activeDesign.cutoff_z?.[1] ?? "");
  }, [activeDesign?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeDesign?.id) return;
    geometryApi.bladeViews(activeDesign.id)
      .then(({ data }) => setViewData(data))
      .catch(() => {});
  }, [activeDesign?.id, activeDesign?.updated_at]);

  const save = async () => {
    if (!activeDesign) return;
    setError(null);
    const r1v = parseFloat(r1), z1v = parseFloat(z1);
    const r2v = parseFloat(r2), z2v = parseFloat(z2);
    if (active && (isNaN(r1v) || isNaN(z1v) || isNaN(r2v) || isNaN(z2v))) {
      setError("Both cut-off points must be valid numbers when cut-off is active.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await bladesApi.update(activeDesign.id, {
        cutoff_active: active,
        cutoff_r: active ? [r1v, r2v] : null,
        cutoff_z: active ? [z1v, z2v] : null,
      });
      setActiveDesign(data);
    } catch (e) {
      setError(e?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  useSaveShortcut(save, { disabled: saving || isReadOnly });

  if (!activeDesign) return <div className="p-6 text-sm text-gray-500">No design loaded.</div>;

  const disabled = isReadOnly || !active;

  // Chart traces
  const bgTraces = (viewData?.hub_shroud || []).map((l, i) => ({
    type: "scatter", mode: "lines", name: l.name,
    x: l.r, y: l.z,
    line: { color: LINE_COLORS[i % LINE_COLORS.length] + "55", width: 1.5 },
  }));

  const r1v = parseFloat(r1), z1v = parseFloat(z1);
  const r2v = parseFloat(r2), z2v = parseFloat(z2);
  const cutoffTrace = (!isNaN(r1v) && !isNaN(z1v) && !isNaN(r2v) && !isNaN(z2v)) ? [{
    type: "scatter", mode: "lines+markers", name: "Cut-off",
    x: [r1v, r2v], y: [z1v, z2v],
    line: { color: "#111827", width: 2, dash: "dash" },
    marker: { color: "#111827", size: 8 },
  }] : [];

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cut-off Settings</p>

        {/* Enable toggle */}
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-sm text-gray-500">Cut-off active</span>
          <input
            type="checkbox"
            checked={active}
            disabled={isReadOnly}
            onChange={e => setActive(e.target.checked)}
            className="rounded w-4 h-4 accent-bh-600"
          />
        </div>

        {/* Point 1 */}
        <div className="flex items-center gap-6">
          <span className="text-sm text-gray-500 w-14 shrink-0">Point 1</span>
          <NumInput label="r [mm]" value={r1} onChange={setR1} disabled={disabled} />
          <NumInput label="z [mm]" value={z1} onChange={setZ1} disabled={disabled} />
        </div>

        {/* Point 2 */}
        <div className="flex items-center gap-6">
          <span className="text-sm text-gray-500 w-14 shrink-0">Point 2</span>
          <NumInput label="r [mm]" value={r2} onChange={setR2} disabled={disabled} />
          <NumInput label="z [mm]" value={z2} onChange={setZ2} disabled={disabled} />
        </div>

        <p className="text-xs text-gray-400">
          The cut-off is a cone of revolution defined by two points in the meridional (r, z) plane.
          Profiles that intersect are trimmed; profiles that end before the cone are extended.
        </p>

        {error && <p className="text-xs text-red-500">{error}</p>}

        {!isReadOnly && (
          <button
            onClick={save}
            disabled={saving}
            className="text-xs bg-bh-600 text-white rounded px-3 py-1.5 hover:bg-bh-700 disabled:opacity-50 transition-colors"
          >
            {saving ? "Saving…" : <>{`Save `}<kbd className="text-[10px] font-sans font-normal opacity-55">Ctrl+S</kbd></>}
          </button>
        )}
      </div>

      {/* Preview chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Hub – Shroud Preview</p>
        <div style={{ height: 340 }}>
          <Plot
            data={[...bgTraces, ...cutoffTrace]}
            layout={{
              margin: { l: 55, r: 14, t: 10, b: 45 },
              xaxis: { title: "r [mm]", gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af", tickfont: { size: 10 }, zeroline: false, showspikes: true, spikemode: "across", spikecolor: "#9ca3af", spikethickness: 1, spikedash: "dot" },
              yaxis: { title: "z [mm]", gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af", tickfont: { size: 10 }, zeroline: false, showspikes: true, spikemode: "across", spikecolor: "#9ca3af", spikethickness: 1, spikedash: "dot", scaleanchor: "x", scaleratio: 1 },
              paper_bgcolor: "transparent",
              plot_bgcolor: "#f9fafb",
              showlegend: true,
              legend: { font: { size: 10 }, bgcolor: "rgba(255,255,255,0.88)", bordercolor: "#e5e7eb", borderwidth: 1, x: 0, xanchor: "left", y: 1, yanchor: "top" },
              hovermode: "closest",
              dragmode: "pan",
            }}
            config={{
              displayModeBar: "hover",
              modeBarButtons: [["zoom2d", "pan2d", "autoScale2d", "resetScale2d", "toImage"]],
              scrollZoom: true,
              responsive: true,
              displaylogo: false,
            }}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler
          />
        </div>
      </div>
    </div>
  );
}
